/**
 * useDevices - Hook para gerenciar dispositivos
 *
 * Features:
 * - Fetch com loading state
 * - Polling automático (30s)
 * - Enriquecimento com positions, groups, drivers (linked via permissions)
 * - Estatísticas calculadas
 * - Refresh manual
 */

import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import { useSelector, shallowEqual } from 'react-redux';

const POLLING_INTERVAL = 30000; // 30 seconds

const useDevices = (options = {}) => {
  const {
    pollingInterval = POLLING_INTERVAL,
    enablePolling = true,
  } = options;

  // Redux stores - get cached devices first for instant display
  const cachedDevices = useSelector((state) => Object.values(state.devices.items || {}), shallowEqual);
  const groups = useSelector((state) => state.groups.items, shallowEqual);
  const drivers = useSelector((state) => state.drivers.items, shallowEqual);
  const positionsById = useSelector((state) => state.session.positions || {}, shallowEqual);

  // State - initialize with cache to avoid skeleton flash
  const [devices, setDevices] = useState(cachedDevices);
  const [deviceDriverLinks, setDeviceDriverLinks] = useState({}); // deviceId -> driverId[]
  const [loading, setLoading] = useState(cachedDevices.length === 0); // Only loading if no cache
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [timestamp, setTimestamp] = useState(Date.now());

  // Fetch devices
  const fetchDevices = useCallback(async (isRefresh = false) => {
    // If we have cached data, treat as refresh (background update) instead of loading
    const hasCachedData = devices.length > 0;
    if (isRefresh || hasCachedData) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await fetch('/api/devices');
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const devicesData = await response.json();
      setDevices(devicesData);
    } catch (err) {
      setError(err.message || 'Erro ao carregar dispositivos');
      console.error('useDevices fetch error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [devices.length]);

  // Clear driver links cache (call after link/unlink operations)
  const clearDriverLinksCache = useCallback((deviceIds = null) => {
    if (deviceIds) {
      // Clear only specific device IDs
      setDeviceDriverLinks((prev) => {
        const next = { ...prev };
        deviceIds.forEach((id) => delete next[id]);
        return next;
      });
    } else {
      // Clear all
      setDeviceDriverLinks({});
    }
  }, []);

  // Fetch device-driver links for specific device IDs
  // Returns drivers with full data (id, name, uniqueId) directly from API
  const fetchDriverLinksForDevices = useCallback(async (deviceIds, forceRefresh = false) => {
    if (!deviceIds?.length) return;

    // If forceRefresh, clear cache for these IDs first
    if (forceRefresh) {
      setDeviceDriverLinks((prev) => {
        const next = { ...prev };
        deviceIds.forEach((id) => delete next[id]);
        return next;
      });
    }

    // Fetch drivers for each device
    try {
      const responses = await Promise.all(
        deviceIds.slice(0, 25).map((deviceId) =>
          fetch(`/api/drivers?deviceId=${deviceId}`)
            .then((res) => (res.ok ? res.json() : []))
            .then((driversList) => ({ deviceId, drivers: driversList }))
            .catch(() => ({ deviceId, drivers: [] }))
        )
      );

      // Update state with fetched data
      setDeviceDriverLinks((current) => {
        const newLinks = { ...current };
        responses.forEach(({ deviceId, drivers: driversList }) => {
          newLinks[deviceId] = driversList;
        });
        return newLinks;
      });
    } catch (err) {
      console.warn('Could not fetch driver links:', err);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchDevices();
  }, [timestamp, fetchDevices]);

  // Polling
  useEffect(() => {
    if (!enablePolling) return undefined;

    const interval = setInterval(() => {
      fetchDevices(true);
    }, pollingInterval);

    return () => clearInterval(interval);
  }, [fetchDevices, pollingInterval, enablePolling]);

  // Enrich devices with positions, groups, drivers
  const enrichedDevices = useMemo(() => devices.map((device) => {
    const position = positionsById[device.id];
    const group = device.groupId ? groups[device.groupId] : null;

    // Get driver from position (logged in via iButton/RFID)
    const driverUniqueId = position?.attributes?.driverUniqueId;
    let driver = driverUniqueId ? drivers[driverUniqueId] : null;
    let driverName = driver?.name || null;

    // If no driver from position, check linked drivers (from API fetch)
    // deviceDriverLinks now contains full driver objects, not just IDs
    const linkedDrivers = deviceDriverLinks[device.id] || [];
    if (!driverName && linkedDrivers.length > 0) {
      // Get first linked driver (already has name from API)
      const linkedDriver = linkedDrivers[0];
      if (linkedDriver?.name) {
        driver = linkedDriver;
        driverName = linkedDriver.name;
      }
    }

    return {
      ...device,
      position,
      driver,
      group,
      // Computed fields
      driverName: driverName || '-',
      groupName: group?.name || '-',
      linkedDrivers, // Full driver objects
      speed: position?.speed || 0,
      speedKmh: Math.round((position?.speed || 0) * 1.852),
      ignition: position?.attributes?.ignition,
      blocked: position?.attributes?.blocked,
      alarm: position?.attributes?.alarm,
      address: position?.address || '-',
      plate: device.attributes?.form?.plate || '-',
    };
  }), [devices, positionsById, drivers, groups, deviceDriverLinks]);

  // Statistics
  const stats = useMemo(() => {
    const online = devices.filter((d) => d.status === 'online').length;
    const offline = devices.filter((d) => d.status === 'offline').length;
    const unknown = devices.filter((d) => d.status === 'unknown').length;
    const moving = enrichedDevices.filter((d) => d.speedKmh > 0).length;
    const withAlarm = enrichedDevices.filter((d) => d.alarm).length;

    return {
      total: devices.length,
      online,
      offline,
      unknown,
      moving,
      withAlarm,
    };
  }, [devices, enrichedDevices]);

  // Manual refresh
  const refresh = useCallback(() => {
    setTimestamp(Date.now());
  }, []);

  // CRUD operations
  const deleteDevice = useCallback(async (id) => {
    await fetch(`/api/devices/${id}`, { method: 'DELETE' });
    refresh();
  }, [refresh]);

  const updateDevice = useCallback(async (id, data) => {
    await fetch(`/api/devices/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    refresh();
  }, [refresh]);

  const bulkDelete = useCallback(async (ids) => {
    const results = await Promise.all(
      ids.map(async (id) => {
        const response = await fetch(`/api/devices/${id}`, { method: 'DELETE' });
        return { id, ok: response.ok, status: response.status };
      }),
    );
    refresh();
  }, [refresh]);

  const bulkMoveToGroup = useCallback(async (ids, groupId) => {
    await Promise.all(ids.map(async (id) => {
      const device = devices.find((d) => d.id === id);
      if (device) {
        await fetch(`/api/devices/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...device, groupId: groupId || 0 }),
        });
      }
    }));
    refresh();
  }, [devices, refresh]);

  return {
    // Data
    devices: enrichedDevices,
    rawDevices: devices,
    stats,

    // State
    loading,
    refreshing,
    error,

    // Actions
    refresh,
    deleteDevice,
    updateDevice,
    bulkDelete,
    bulkMoveToGroup,
    fetchDriverLinksForDevices,
    clearDriverLinksCache,

    // Helpers
    getDeviceById: (id) => enrichedDevices.find((d) => d.id === id),
  };
};

export default useDevices;
